/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import React, { FunctionComponent, ReactElement } from "react";
import { Stack, IStackTokens, IIconProps, Text } from "@fluentui/react";
import { CompoundButton, IButtonStyles } from "@fluentui/react/lib/Button";
import CreateNewDefinition from "./CreateNewDefinition";
import styles from "./StartView.module.css";
import { useTranslation } from "react-i18next";
import { IMainFrameViewModel } from "../Mainframe/MainframeViewModel";
import { IStartViewState, StartViewCommands } from "./StartViewViewModel";
import ViewHeader from "../MainView/ViewHeader";
import { openSettingsDefinitionFile } from "../Utilities/FileOperations";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";

const logger: ILogger = getLogger();

/**
 * Interface for props to DefinitionSelection component.
 */
interface IStartViewProps {
    mainFrameVM: IMainFrameViewModel;
}

const startViewCommands = new StartViewCommands();
const cmds = startViewCommands.Cmds;

const stackTokens: IStackTokens = { childrenGap: 40 };
const importIcon: IIconProps = {
    iconName: "OpenFile"
};
const circleAddIcon: IIconProps = { iconName: "CircleAddition" };
const buttonStyle: IButtonStyles = {
    flexContainer: {
        display: "inline"
    },
    root: {
        height: 200,
        width: 322
    },
    textContainer: {
        textAlign: "center"
    }
};

/**
 * Component for selecting Hierarchical Settings definition file  .
 */
const StartView: FunctionComponent<IStartViewProps> = (props): ReactElement => {
    const { t } = useTranslation();
    const initialState: IStartViewState = {
        errorImportMessage: "",
        showCreateDefDialog: false,
        parentVM: props.mainFrameVM
    };
    const [state, dispatch] = React.useReducer(startViewCommands.Reducer, initialState);
    const vm = { state, cmds, dispatch };

    const importSettingsDefinitionHandler = async () => {
        try {
            const definition = await openSettingsDefinitionFile("application/json");
            state.parentVM.dispatch(state.parentVM.cmds.openMainView(definition));
        } catch (ex) {
            if (ex instanceof Error) {
                logger.logException(ex, "Error loading file");
            }
        }
    };

    const createNewSettingDefinitionHandler = () => {
        dispatch(cmds.showCreateDefinitionDialog());
    };

    return (
        <>
            <ViewHeader />
            <div className={styles.startView}>
                <h2>
                    <Text variant="xxLarge">Settings designer</Text>
                </h2>
                <Stack
                    tokens={stackTokens}
                    className={styles.centerDiv}
                    horizontal
                    verticalAlign="center"
                    horizontalAlign="center"
                >
                    <CompoundButton
                        styles={buttonStyle}
                        iconProps={importIcon}
                        text={t("ImportSettingsDefinition")}
                        onClick={importSettingsDefinitionHandler}
                        className="ms-depth-8 ms-bgColor-gray30 ms-fontSize-18"
                    ></CompoundButton>
                    <CompoundButton
                        styles={buttonStyle}
                        iconProps={circleAddIcon}
                        text={t("CreateNewDefinition")}
                        onClick={createNewSettingDefinitionHandler}
                        className="ms-depth-8 ms-bgColor-gray30 ms-fontSize-18"
                    ></CompoundButton>
                </Stack>
            </div>
            <CreateNewDefinition startViewVM={vm} />
        </>
    );
};

export default StartView;
